import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

// One-time-per-window guard for auto-reloading after a stale-chunk failure.
// We store the timestamp of the last auto-reload rather than a single-shot
// boolean: every genuine deployment that strands an open tab must be able to
// self-heal, but a chunk that still fails immediately after a reload must NOT
// loop forever. A short cool-down window gives us both.
const RELOAD_TS_KEY = 'chunk_reload_ts';
const RELOAD_WINDOW_MS = 10_000;

// Detects the "failed to fetch dynamically imported module" family of errors
// across Chrome, Firefox, and Safari. These occur when a stale index.html
// references chunk hashes that no longer exist after a new deployment.
//
// When the missing chunk is served by an SPA catch-all, the response is
// index.html rather than a 404, so the browser reports a MIME-type mismatch
// instead: Safari throws TypeError "'text/html' is not a valid JavaScript MIME
// type", Chrome "Expected a JavaScript module script but the server responded
// with a MIME type of \"text/html\"", Firefox "blocked because of a disallowed
// MIME type". Vite's CSS preloader rejects with "Unable to preload CSS for
// /assets/X.css" when a stale chunk's stylesheet is gone. Same stale
// deployment, different wording — all self-heal.
export function isChunkLoadError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('mime type') ||
    msg.includes('unable to preload css') ||
    (msg.includes('dynamically imported module') && (msg.includes('failed') || msg.includes('error')))
  );
}

// True when we have NOT auto-reloaded within the cool-down window, i.e. it is
// safe to attempt another recovery reload. Read-only — safe to call from
// React's getDerivedStateFromError.
export function canReloadForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);
    return Date.now() - last > RELOAD_WINDOW_MS;
  } catch {
    return true;
  }
}

// Navigation seam: indirection around the actual page reload so tests can
// observe it without redefining jsdom's read-only window.location.
export const navigation = {
  reload(): void {
    window.location.reload();
  },
};

// Attempts a full-page reload to pick up the fresh index.html (and therefore
// the current chunk hashes). Returns true if the reload was triggered, false if
// it was suppressed by the cool-down window (caller should surface a fallback).
export function reloadForChunkError(): boolean {
  if (!canReloadForChunkError()) return false;
  try {
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — reload anyway.
  }
  navigation.reload();
  return true;
}

// Drop-in replacement for React.lazy that self-heals stale-chunk failures.
// On a chunk-load error it triggers a guarded full-page reload and returns a
// never-resolving promise so Suspense keeps showing its loader instead of
// flashing the error boundary while the reload takes over. Non-chunk errors and
// chunk errors during the cool-down rethrow to the ErrorBoundary as usual.
// `ComponentType<any>` mirrors React.lazy's own signature — components carry
// arbitrary prop shapes, so a narrower constraint rejects valid pages.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && reloadForChunkError()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
